<template>
  <div class="n-input" :class="{ 'has-error': errorMessage }">
    <fieldset :disabled="disabled" :id="inputId" :name="name" class="radio-button-group">
      <legend :class="{ 'sr-only': hideLabel }">{{ label }}</legend>
      <div class="radio-button" v-for="(option, index) in options" :key="option.label">
        <input
          type="radio"
          :value="option.value"
          :disabled="option.disabled"
          :id="`${inputId}__${index}`"
          v-model="selected"
        />
        <label
          class="radio-button__label"
          :class="{
            'radio-button__label--checked': selected === option.value,
            'radio-button__label--disabled': option.disabled || disabled,
          }"
          :for="`${inputId}__${index}`"
        >
          {{ option.label }}
        </label>
      </div>
    </fieldset>
    <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
  </div>
</template>
<script lang="ts" setup>
import { PropType, ref, watch } from 'vue';
import { useIdGenerator } from '@/shared/designsystem/useIdGenerator';
import { useVModel } from '@vueuse/core';

export type NRadioButton = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

defineOptions({
  name: 'n-radio-buttons',
});

const props = defineProps({
  id: {
    type: String,
  },
  label: {
    type: String,
  },
  name: {
    type: String,
  },
  modelValue: {
    type: [String, Number],
    required: true,
  },
  options: {
    type: Array as PropType<NRadioButton[]>,
    required: true,
  },
  errorMessage: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideLabel: {
    type: String,
  },
});

const { inputId } = useIdGenerator(props.id);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void;
  (e: 'change', value: string | number): void;
}>();

const selected = useVModel(props, 'modelValue', emit);
</script>

<style lang="scss" scoped>
@use '../../../styles/accessibility' as *;
@use '../../../styles/colours' as *;
@use '../../../styles/input' as *;
.radio-button-group {
  padding: 0;
  border: none;
  legend {
    margin-bottom: 0.5rem;
  }
}
.radio-button {
  margin-bottom: 0.75rem;
  &:last-of-type {
    margin-bottom: 0;
  }
  input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
  .radio-button__label {
    padding-left: 2rem;
    position: relative;
    font-size: 1rem;
    line-height: 1.5rem;
    margin: 0.5rem;
    margin-left: 0;
    cursor: pointer;
    &::before {
      position: absolute;
      left: 0;
      top: 0rem;
      content: '';
      border-radius: 50%;
      width: 1.5rem;
      height: 1.5rem;
      border: 0.1rem solid colourScheme('grey', 'light');
    }
    &:hover {
      &::before {
        border-color: colourScheme('deep-sea');
      }
    }
    &--checked {
      &::after {
        position: absolute;
        content: '';
        top: 0;
        border-radius: 50%;
        background: colourScheme('deep-sea');
        left: 0;
        transform: scale(0.5);
        width: 1.5rem;
        height: 1.5rem;
        display: inline-block;
      }
    }
    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      &:hover {
        &::before {
          border-color: colourScheme('grey', 'light');
        }
      }
    }
  }
}
.error-message {
  padding-left: 0.25rem;
}
</style>
